import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip,
  CircularProgress,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

// Helper function to map database values to display text
const mapValueToLabel = (value, tr) => {
  const valueMap = {
    'excellent': { label: tr('優良', 'Excellent'), color: 'success', rate: '>95%' },
    'good': { label: tr('良好', 'Good'), color: 'primary', rate: '85-95%' },
    'acceptable': { label: tr('可接受', 'Acceptable'), color: 'info', rate: '70-85%' },
    'needsImprovement': { label: tr('需改進', 'Needs improvement'), color: 'warning', rate: '50-70%' },
    'insufficient': { label: tr('不足', 'Insufficient'), color: 'error', rate: '<50%' }
  };
  
  return valueMap[value] || { label: value, color: 'default', rate: '' };
};

// Helper function to extract selected issues from JSON object
const getSelectedIssues = (issuesObj, tr) => {
  if (!issuesObj) return [];
  
  try {
    // If the data is a string, parse it
    const issues = typeof issuesObj === 'string' ? JSON.parse(issuesObj) : issuesObj;
    
    // Issue name mappings
    const issueLabels = {
      // Teaching issues
      scriptMismatch: tr('建議內容與腳本不符', 'Guidance does not match the script'),
      impreciseContent: tr('建議內容不夠精確', 'Guidance is not precise enough'),
      inappropriateContext: tr('建議不適用當前情境', 'Guidance does not fit the scenario'),
      ambiguousContent: tr('建議內容語意模糊', 'Guidance is ambiguous'),
      
      // Node issues
      falsePositive: tr('誤判判斷為達標', 'Incorrectly marked complete'),
      falseNegative: tr('誤判判斷為未達標', 'Incorrectly marked incomplete'),
      inconsistentJudgment: tr('誤判判斷不一致', 'Inconsistent judgment'),
      delayedJudgment: tr('誤判判斷延遲', 'Delayed judgment'),
      
      // Error correction issues
      missedErrors: tr('未能識別特定類型錯誤', 'Failed to identify a specific error type'),
      noSuggestions: tr('未能提供修改建議', 'No correction suggested'),
      noContext: tr('未能提供使用情境', 'No usage context provided'),
      unclearSuggestions: tr('建議過於不明確', 'Suggestion is unclear'),
      impracticalSuggestions: tr('系統建議不實用', 'Suggestion is impractical'),
      
      // Checkpoint issues
      falsePass: tr('查核點判斷為達標', 'Incorrectly marked checkpoint complete'),
      falseFail: tr('查核點判斷為未達標', 'Incorrectly marked checkpoint incomplete'),
      unreasonableDelay: tr('查核點延遲不合理', 'Unreasonable checkpoint delay'),
      unclearDelay: tr('查核點延遲不明確', 'Unclear checkpoint delay'),
      
      // Common
      other: tr('其他', 'Other')
    };
    
    // Extract regular selected issues
    const selectedIssues = [];
    
    Object.entries(issues).forEach(([key, value]) => {
      if (key === 'other' && value === true) {
        selectedIssues.push({
          key: 'other',
          label: issueLabels.other,
          isDetails: false
        });
      }
      else if (key !== 'otherDetails' && key !== 'other' && value === true) {
        selectedIssues.push({
          key,
          label: issueLabels[key] || key,
          isDetails: false
        });
      }
    });
    
    // Add other details if the 'other' option is selected
    if (issues.other === true && issues.otherDetails) {
      selectedIssues.push({
        key: 'otherDetails',
        label: issues.otherDetails,
        isDetails: true
      });
    }
    
    return selectedIssues;
  } catch (err) {
    console.error('Error parsing issues:', err);
    return [];
  }
};

const POCEvaluationResults = () => {
  const { tr, isEnglish } = useLanguage();
  const [evaluations, setEvaluations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memberId] = useState(() => localStorage.getItem('memberId'));

  // Load evaluations when component mounts
  useEffect(() => {
    fetchEvaluations();
  }, []);

  // Function to fetch evaluations from the database
  const fetchEvaluations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/storage/poc-evaluations`, {
        params: memberId ? { member_id: parseInt(memberId) } : {}
      });
      setEvaluations(response.data);
      
    } catch (err) {
      console.error('Error fetching evaluations:', err);
      setError(`Failed to fetch evaluations: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    // Create a date object that's explicitly in the Asia/Taipei timezone
    const date = new Date(timestamp);
    return date.toLocaleString(isEnglish ? 'en-US' : 'zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Taipei'
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            {tr('POC 系統評估結果', 'POC System Evaluation Results')}
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchEvaluations}
            disabled={isLoading}
          >
            {tr('刷新資料', 'Refresh')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : evaluations.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1">
              {tr('尚無評估資料', 'No evaluation data yet')}
            </Typography>
          </Paper>
        ) : (
          <>
            {evaluations.map((evaluation) => {
              // Get display info for each rating
              const teachingInfo = mapValueToLabel(evaluation.teaching_accuracy, tr);
              const nodeInfo = mapValueToLabel(evaluation.node_evaluation, tr);
              const errorInfo = mapValueToLabel(evaluation.error_correction_evaluation, tr);
              const checkpointInfo = mapValueToLabel(evaluation.checkpoint_evaluation, tr);
              
              // Extract selected issues
              const teachingIssues = getSelectedIssues(evaluation.teaching_issues, tr);
              const nodeIssues = getSelectedIssues(evaluation.node_issues, tr);
              const errorCorrectionIssues = getSelectedIssues(evaluation.error_correction_issues, tr);
              const checkpointIssues = getSelectedIssues(evaluation.checkpoint_issues, tr);
              
              // Check if there are any selected issues
              const hasIssues = teachingIssues.length > 0 || nodeIssues.length > 0 || 
                               errorCorrectionIssues.length > 0 || checkpointIssues.length > 0;
              
              return (
                <Paper key={evaluation.id} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      {tr('評估時間', 'Evaluated at')}: {formatTimestamp(evaluation.created_at)}
                    </Typography>
                    <Typography variant="body1">
                      {evaluation.member_name ? `${tr('提交者', 'Submitted by')}: ${evaluation.member_name}` : `${tr('提交者', 'Submitted by')}: (${tr('訪客', 'Guest')})`}
                    </Typography>
                  </Box>
                  
                  {evaluation.conversation_id && (
                    <Box sx={{ mb: 2 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => window.open(`/history?view=${evaluation.conversation_id}`, '_blank')}
                      >
                        {tr('查看相關對話', 'View conversation')} #{evaluation.conversation_id}
                      </Button>
                    </Box>
                  )}
                  
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>{tr('評估項目', 'Evaluation Item')}</TableCell>
                          <TableCell>{tr('評分', 'Rating')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>{tr('教學引導準確性', 'Coaching Guidance Accuracy')}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${teachingInfo.label} (${teachingInfo.rate})`} 
                              color={teachingInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{tr('節點判斷正確性', 'Node Judgment Accuracy')}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${nodeInfo.label} (${nodeInfo.rate})`} 
                              color={nodeInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{tr('評估報告糾錯狀況', 'Report Error Detection and Correction')}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${errorInfo.label} (${errorInfo.rate})`} 
                              color={errorInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{tr('查核點判斷準確性', 'Checkpoint Judgment Accuracy')}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${checkpointInfo.label} (${checkpointInfo.rate})`} 
                              color={checkpointInfo.color} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {hasIssues && (
                    <Accordion sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight="bold">{tr('已選擇的問題', 'Selected Issues')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {teachingIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">{tr('教學引導問題:', 'Coaching guidance issues:')}</Typography>
                            <List dense disablePadding>
                              {teachingIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? tr('其他', 'Other') : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {nodeIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">{tr('節點判斷問題:', 'Node judgment issues:')}</Typography>
                            <List dense disablePadding>
                              {nodeIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? tr('其他', 'Other') : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {errorCorrectionIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">{tr('評估報告糾錯問題:', 'Report correction issues:')}</Typography>
                            <List dense disablePadding>
                              {errorCorrectionIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? tr('其他', 'Other') : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {checkpointIssues.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">{tr('查核點判斷問題:', 'Checkpoint judgment issues:')}</Typography>
                            <List dense disablePadding>
                              {checkpointIssues.map((issue, index) => (
                                <ListItem key={index} disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: issue.isDetails ? 2 : 0 }}>
                                  <ListItemText primary={`• ${issue.isDetails ? tr('其他', 'Other') : issue.label}`} />
                                  {issue.isDetails && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, ml: 2, width: '100%', backgroundColor: '#f9f9f9' }}>
                                      <Typography variant="body2">
                                        {issue.label}
                                      </Typography>
                                    </Paper>
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}
                  
                  {evaluation.technical_suggestions && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{tr('技術建議:', 'Technical suggestions:')}</Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="body2">
                          {evaluation.technical_suggestions}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </>
        )}
      </Box>
    </Container>
  );
};

export default POCEvaluationResults;
